import { useRef, useEffect } from "react";
import { useReactToPrint } from "react-to-print";
import "./PdfTcleStyle.scss";
import moment from "moment/moment";
import "moment/locale/pt";

moment.locale("pt");

// Espaço de preenchimento: mostra o valor (sem linha) quando há dados,
// ou uma linha sublinhada para preenchimento manual quando está vazio.
const Blank = ({ value, tamanho }) => (
  <span
    className={`pdf-tcle__blank pdf-tcle__blank--${tamanho}${
      value ? " pdf-tcle__blank--preenchido" : ""
    }`}
  >
    {value || ""}
  </span>
);

function PdfTcle({ tcleData, showPreviousPage }) {
  const componentRef = useRef();
  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    documentTitle: `TCLE-${tcleData.cliente.nome}_${moment(new Date()).format(
      "DD-MM-YYYY",
    )}`,
    onAfterPrint: () => {
      showPreviousPage();
    },
  });

  useEffect(() => {
    window.scrollTo(0, 0);
    handlePrint();
  }, [handlePrint]);

  const isPretoebranco =
    tcleData.clinica?.configuracoes?.configuracoes?.impressaoPretoEBranco
      ?.ativo;

  const dataAtestado = tcleData.data_tcle ? moment(tcleData.data_tcle) : null;
  const dia = dataAtestado ? dataAtestado.format("DD") : "";
  const mes = dataAtestado ? dataAtestado.format("MM") : "";
  const ano = dataAtestado ? dataAtestado.format("YYYY") : "";

  return (
    <div
      ref={componentRef}
      className={`pdf-tcle-wrapper${isPretoebranco ? " pdf-wrapper--pretoebranco" : ""}`}
    >
      <div className="pdf-tcle-body">
        {/* HEADER */}
        <div className="pdf-tcle__cabecalho">
          <img
            src={`${process.env.REACT_APP_BACKEND_LINK}/${tcleData.clinica.logotipo}`}
            alt={tcleData.clinica.nome}
            className="pdf-tcle__cabecalho__logo"
          />
        </div>

        {/* TITLE */}
        <h2 className="pdf-tcle__title">
          TERMO DE CONSENTIMENTO LIVRE ESCLARECIDO (TCLE) PARA TRATAMENTOS
          ODONTOLÓGICOS
        </h2>

        {/* SECTION 1 — Patient + dentist identification */}
        <div className="pdf-tcle__section pdf-tcle__texto">
          Eu, <span style={{ fontWeight: "700" }}>{tcleData.cliente.nome}</span>
          ,CNI <span style={{ fontWeight: "700" }}>{tcleData.cliente.cni}</span>
          , declaro que fui informado(a) pelo(a) cirurgião(ã)-dentista{" "}
          <span style={{ fontWeight: "700" }}>
            {tcleData.medico?.name || "___________________________________"}
          </span>
          ,CIP _____________________, que o tratamento proposto está sujeito aos
          seguintes riscos e intercorrências:
          {/* Risks lines — sem campo de dados, sempre para preenchimento manual */}
          <div className="pdf-tcle__blank-area">
            <span className="pdf-tcle__blank-line" />
            <span className="pdf-tcle__blank-line" />
            <span className="pdf-tcle__blank-line--dot" />
          </div>
        </div>

        {/* SECTION 2 — Commitments */}
        <div className="pdf-tcle__section">
          <p className="pdf-tcle__texto pdf-tcle__texto--justified">
            Assumo o compromisso de comparecer às consultas/sessões agendadas,
            de buscar marcar os atendimentos de forma contínua, sem interrupções
            injustificadas, de seguir as recomendações/prescrições diversas,
            sejam estas passadas para antes ou para após atendimentos (pré e/ou
            pós-operatórias), de tomar os devidos cuidados com a higienização
            bucal e com a região tratada, a cada procedimento realizado, em
            atenção às orientações que receber durante o tratamento e em
            cuidados preventivos após este.
          </p>
        </div>

        {/* SECTION 3 — Individual factors */}
        <div className="pdf-tcle__section">
          <p className="pdf-tcle__texto pdf-tcle__texto--justified">
            Além dos fatores acima, fui esclarecido(a) que o tratamento tem um
            índice de insucesso e, como todos os procedimentos de saúde, o
            resultado esperado também poderá não se concretizar devido a fatores
            individuais, como a resposta biológica, e limitações da ciência,
            além de outras variações de ordem local ou sistêmica, como:
            {tcleData.fatores_individuais && (
              <Blank value={tcleData.fatores_individuais} tamanho="longa" />
            )}
          </p>
          {!tcleData.fatores_individuais && (
            <span className="pdf-tcle__blank-line" />
          )}
        </div>

        {/* SECTION 4 — Health history + legal */}
        <div className="pdf-tcle__section">
          <p className="pdf-tcle__texto pdf-tcle__texto--justified">
            <Blank value={tcleData.historico_saude} tamanho="media" />. Informo
            que discuti com o(a) cirurgião(ã)-dentista minha história de saúde
            geral, inclusive as doenças conhecidas por mim. Declaro, outrossim,
            com base no Código de Defesa do Consumidor a Lei n.º 88/V/1998, de
            31 de dezembro, que aprova o regime jurídico de proteção e defesa
            dos consumidores, que além das possíveis intercorrências citadas
            acima, fui devidamente informado(a) sobre os propósitos dos
            procedimentos e seus custos. Quanto às alternativas de tratamento,
            fui esclarecido(a) sobre as vantagens e desvantagens de outras
            técnicas, tais como:
          </p>
          {!tcleData.tecnicas_alternativas && (
            <span className="pdf-tcle__blank-line" />
          )}
        </div>

        {/* SECTION 5 — Guide + pre/post care */}
        <div className="pdf-tcle__section">
          <p className="pdf-tcle__texto pdf-tcle__texto--justified">
            <Blank value={tcleData.tecnicas_alternativas} tamanho="curta" /> e
            optei pela proposta descrita na Guia de Tratamento Odontológica n°{" "}
            <Blank value={tcleData.numero_guia} tamanho="media" />. Fui
            orientado(a) sobre as seguintes condições e cuidados pré e
            pós-operatórios necessários para pleno êxito do tratamento:
            {tcleData.cuidados_pos_operatorios && (
              <Blank value={tcleData.cuidados_pos_operatorios} tamanho="longa" />
            )}
          </p>
          {!tcleData.cuidados_pos_operatorios && (
            <span className="pdf-tcle__blank-line" />
          )}
        </div>

        {/* SECTION 6 — Acceptance */}
        <div className="pdf-tcle__section">
          <p className="pdf-tcle__texto pdf-tcle__texto--justified">
            Portanto, aceito e autorizo a execução do tratamento,
            comprometendo-me a seguir rigorosamente as orientações do(a)
            cirurgião(ã)-dentista, comunicando imediatamente qualquer alteração
            em decorrência dos procedimentos realizados e comparecer
            pontualmente às consultas marcadas. Tenho ciência de que o(a)
            cirurgião(ã)-dentista se compromete a utilizar as técnicas e os
            materiais adequados à execução do tratamento.
          </p>
        </div>

        {/* LOCAL + DATE */}
        <div className="pdf-tcle__local-data">
          <span>
            Local, {tcleData.clinica?.ilha || "_________________"} Data {dia} /
            {mes} /{ano}
          </span>
        </div>

        {/* SIGNATURES */}
        <div className="pdf-tcle__assinaturas">
          <div className="pdf-tcle__assinaturas-dupla">
            <div className="pdf-tcle__assinatura-bloco">
              <div className="pdf-tcle__assinatura-linha" />
              <span className="pdf-tcle__assinatura-label">
                RESPONSÁVEL LEGAL
              </span>
            </div>

            <div className="pdf-tcle__assinatura-bloco">
              <div className="pdf-tcle__assinatura-linha" />
              <span className="pdf-tcle__assinatura-label">
                BENEFICIÁRIO(A) / PACIENTE
              </span>
            </div>
          </div>

          <div className="pdf-tcle__assinatura-bloco">
            {tcleData.medico?.assinatura?.ativa &&
            tcleData.medico?.assinatura?.autorizacoes?.atestados &&
            tcleData.medico?.assinatura?.imagem ? (
              <img
                src={`${process.env.REACT_APP_BACKEND_LINK}/${tcleData.medico.assinatura.imagem}`}
                alt="Assinatura"
                className="pdf-tcle__assinatura-img"
              />
            ) : (
              <div className="pdf-tcle__assinatura-linha" />
            )}
            <span className="pdf-tcle__assinatura-label">
              CIRURGIÃO(Ã)-DENTISTA
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PdfTcle;
